<template>
  <div class="mt-3">
    <!-- Create form -->
    <div class="flat-card mb-3">
      <h6 class="mb-3" style="font-weight:600;color:#0f172a;">
        <i class="bi bi-ticket-perforated mr-1"></i> New Promo Code
      </h6>
      <form @submit.prevent="submit">
        <div class="form-row">
          <div class="form-group col-md-3">
            <label class="font-weight-bold">Code</label>
            <input
              v-model="form.code"
              class="form-control text-uppercase"
              placeholder="SAVE20"
              required
              maxlength="30"
              style="letter-spacing:.05em;"
            />
          </div>
          <div class="form-group col-md-2">
            <label class="font-weight-bold">Type</label>
            <select v-model="form.type" class="form-control">
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed (KES)</option>
            </select>
          </div>
          <div class="form-group col-md-2">
            <label class="font-weight-bold">Amount</label>
            <input v-model.number="form.amount" type="number" min="0" step="0.01" class="form-control" required />
          </div>
          <div class="form-group col-md-2">
            <label class="font-weight-bold">Max Uses</label>
            <input v-model.number="form.max_uses" type="number" min="1" class="form-control" placeholder="Unlimited" />
          </div>
          <div class="form-group col-md-3">
            <label class="font-weight-bold">Expires At</label>
            <input v-model="form.expires_at" type="datetime-local" class="form-control" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="font-weight-bold">Plan Restriction (optional)</label>
            <input v-model="form.plan_restriction" class="form-control" placeholder="e.g. Starter (leave blank for all)" />
          </div>
        </div>
        <button class="btn btn-primary" type="submit" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
          Create Code
        </button>
      </form>
    </div>

    <!-- List -->
    <div class="flat-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0" style="font-weight:600;color:#0f172a;">All Promo Codes</h6>
        <button class="btn btn-outline-secondary" @click="fetch" :disabled="loading">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-secondary" role="status"></div>
      </div>

      <div v-else-if="codes.length === 0" class="text-center text-secondary py-3">
        <small>No promo codes yet</small>
      </div>

      <table v-else class="table table-hover mb-0">
        <thead>
          <tr>
            <th>Code</th>
            <th>Discount</th>
            <th class="d-none d-md-table-cell">Usage</th>
            <th class="d-none d-md-table-cell">Plan</th>
            <th class="d-none d-md-table-cell">Expires</th>
            <th>Status</th>
            <th style="width:56px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="code in codes" :key="code.id" :class="!code.active ? 'text-secondary' : ''">
            <td class="align-middle">
              <span class="pill" style="font-size:.8rem;letter-spacing:.05em;">{{ code.code }}</span>
            </td>
            <td class="align-middle">
              <span class="font-weight-bold">
                {{ code.type === 'percent' ? code.amount + '%' : 'KES ' + Number(code.amount).toLocaleString() }}
              </span>
            </td>
            <td class="align-middle d-none d-md-table-cell">
              <small class="text-secondary">
                {{ code.uses_count }} {{ code.max_uses ? '/ ' + code.max_uses : '' }}
                <span v-if="code.max_uses && code.uses_count >= code.max_uses" class="pill pill-danger ml-1">Exhausted</span>
              </small>
            </td>
            <td class="align-middle d-none d-md-table-cell">
              <small class="text-secondary">{{ code.plan_restriction || 'All' }}</small>
            </td>
            <td class="align-middle d-none d-md-table-cell">
              <small :class="isExpired(code) ? 'text-danger' : 'text-secondary'">
                {{ code.expires_at ? formatDate(code.expires_at) : 'Never' }}
              </small>
            </td>
            <td class="align-middle">
              <span :class="['pill', code.active && !isExpired(code) ? 'pill-primary' : 'pill-secondary']">
                {{ code.active && !isExpired(code) ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="align-middle text-right">
              <b-dropdown variant="link" no-caret right toggle-class="text-decoration-none p-0">
                <template #button-content>
                  <i class="bi bi-three-dots-vertical text-secondary"></i>
                </template>
                <b-dropdown-item @click="toggle(code)">
                  <i :class="['bi mr-1', code.active ? 'bi-pause' : 'bi-play']"></i>
                  {{ code.active ? 'Deactivate' : 'Activate' }}
                </b-dropdown-item>
                <b-dropdown-item class="text-danger" @click="remove(code.id)">
                  <i class="bi bi-trash mr-1"></i> Delete
                </b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminCoupons',

  data() {
    return {
      loading: false,
      saving: false,
      codes: [],
      form: {
        code: '',
        type: 'percent',
        amount: '',
        max_uses: '',
        expires_at: '',
        plan_restriction: '',
      },
    };
  },

  mounted() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/promo-codes');
        this.codes = data;
      } finally {
        this.loading = false;
      }
    },

    async submit() {
      this.saving = true;
      try {
        await axios.post('/api/admin/promo-codes', {
          ...this.form,
          code: this.form.code.toUpperCase().trim(),
          max_uses: this.form.max_uses || null,
          expires_at: this.form.expires_at || null,
          plan_restriction: this.form.plan_restriction || null,
        });
        this.$toasts.push({ message: 'Promo code created', type: 'success' });
        this.form = { code: '', type: 'percent', amount: '', max_uses: '', expires_at: '', plan_restriction: '' };
        await this.fetch();
      } catch (e) {
        const msg = e?.response?.data?.errors?.code?.[0] || e?.response?.data?.message || 'Failed to create';
        this.$toasts.push({ message: msg, type: 'error' });
      } finally {
        this.saving = false;
      }
    },

    async toggle(code) {
      try {
        const { data } = await axios.post(`/api/admin/promo-codes/${code.id}/toggle`);
        code.active = data.active;
      } catch (e) {
        this.$toasts.push({ message: 'Failed to toggle', type: 'error' });
      }
    },

    async remove(id) {
      if (!confirm('Delete this promo code?')) return;
      try {
        await axios.delete(`/api/admin/promo-codes/${id}`);
        this.codes = this.codes.filter(c => c.id !== id);
      } catch (e) {
        this.$toasts.push({ message: 'Failed to delete', type: 'error' });
      }
    },

    isExpired(code) {
      return code.expires_at && moment(code.expires_at).isBefore(moment());
    },

    formatDate(date) {
      return moment(date).format('D MMM YY');
    },
  },
};
</script>

<style scoped>
.flat-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 20px;
}
.pill {
  display: inline-block;
  font-size: .7rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #0f172a;
}
.pill-primary { background: rgba(13,110,253,.1); color: #0d6efd; }
.pill-secondary { background: #f1f5f9; color: #64748b; }
.pill-danger { background: rgba(220,53,69,.1); color: #dc3545; }
</style>
